import { z } from 'zod';
import { Timestamp } from './common';
import { NoContent, endpoint } from './endpoint';

/**
 * Hosts of the browsers' push services: Chrome, Edge on Android, Samsung Internet and Opera (FCM),
 * Firefox, Safari (macOS and iOS home-screen apps) and Edge on Windows (WNS). The company server
 * only ever posts to these, so a subscription cannot point it at another address.
 */
const PUSH_SERVICE_HOSTS = ['fcm.googleapis.com', 'updates.push.services.mozilla.com', 'web.push.apple.com'];
const PUSH_SERVICE_SUFFIXES = ['.notify.windows.com', '.push.apple.com'];

export function isPushServiceUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' || url.port !== '' || url.username || url.password) return false;
  return PUSH_SERVICE_HOSTS.includes(url.hostname) || PUSH_SERVICE_SUFFIXES.some((suffix) => url.hostname.endsWith(suffix));
}

/** How often an app in use says so (push.active); the server counts it in use a little longer than that. */
export const ACTIVE_REPORT_MS = 30_000;

const Base64Url = (bytes: number) => z.string().regex(new RegExp(`^[A-Za-z0-9_-]{${Math.ceil((bytes * 4) / 3)}}={0,2}$`));

/** PushSubscription.toJSON() of the browser: where to post and the keys to encrypt for (RFC 8291). */
export const PushSubscriptionBody = z.object({
  endpoint: z.string().max(2048).refine(isPushServiceUrl, 'not a push service address'),
  keys: z.object({
    /** Uncompressed P-256 public key, 65 bytes. */
    p256dh: Base64Url(65),
    /** Authentication secret, 16 bytes. */
    auth: Base64Url(16),
  }),
});
export type PushSubscriptionBody = z.infer<typeof PushSubscriptionBody>;

/**
 * Web push (capability `push.v1`). A subscription belongs to the session that sent it: signing out
 * or losing the session stops the pushes to that browser.
 */
export const pushEndpoints = {
  /**
   * The company server's VAPID public key (base64url, uncompressed P-256), for PushManager.subscribe, and
   * this browser's do-not-disturb end if one is set (`quiet`), so a reloaded page keeps it.
   */
  config: endpoint({
    id: 'push.config',
    method: 'GET',
    path: '/api/v1/push/config',
    auth: 'session',
    response: z.object({ publicKey: z.string(), quietUntil: Timestamp.nullable().optional() }),
  }),
  /** Do-not-disturb of this browser (its session): no pushes until then; null ends it. */
  quiet: endpoint({ id: 'push.quiet', method: 'PUT', path: '/api/v1/push/quiet', auth: 'session', body: z.object({ until: Timestamp.nullable() }), response: NoContent }),
  /**
   * The person is using this app now (capability `push.hold`): sent every ACTIVE_REPORT_MS while they are, and
   * `false` once they stop. Their pushes wait meanwhile, since this app shows new messages itself.
   */
  active: endpoint({ id: 'push.active', method: 'PUT', path: '/api/v1/push/active', auth: 'session', body: z.object({ active: z.boolean() }), response: NoContent }),
  /** Stores this browser's subscription for the session; the same endpoint sent again moves to it. */
  subscribe: endpoint({ id: 'push.subscribe', method: 'PUT', path: '/api/v1/push/subscription', auth: 'session', body: PushSubscriptionBody, response: NoContent }),
  unsubscribe: endpoint({
    id: 'push.unsubscribe',
    method: 'DELETE',
    path: '/api/v1/push/subscription',
    auth: 'session',
    body: z.object({ endpoint: z.string().max(2048) }),
    response: NoContent,
  }),
};

/** What the service worker receives (encrypted end to end: the push service cannot read it). */
export const PushMessage = z.object({
  title: z.string(),
  body: z.string(),
  conversationId: z.string(),
  messageId: z.string(),
  /** The person's unread messages over unmuted conversations, for the app icon. */
  badge: z.number().int().nonnegative().optional(),
  /** The message was deleted: its notification, if still shown, is replaced quietly (`body` says so). */
  deleted: z.literal(true).optional(),
});
export type PushMessage = z.infer<typeof PushMessage>;
